import React, { useEffect, useState, useCallback } from 'react'
import { DataGrid } from '@mui/x-data-grid';
import { Card, CardContent, Chip, Container, Tooltip } from '@mui/material';
import { useStoreState } from 'pullstate';
import { AppStore } from '../../stores/AppStore.js';
import dataGridStyles from '../../components/styles/dataGridStyles.js';
import SearchField from '../../components/SearchField.jsx'

const CliftonStrengths = () => {
    const [rows, setRows] = useState([]);
    const [rowsFiltered, setRowsFiltered] = useState([]);
    const strengths = useStoreState(AppStore, s => s.strengths);
    const [searchValue, setSearchValue] = useState('');
    const [pageSizes, setPageSizes] = useState(10);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: pageSizes,
        page: 0,
    });
    const columns = [
        { field: 'name', headerName: 'Name', width: 300, headerClassName: 'header-class' },
        {
            field: 'strength_1',
            headerName: 'Strength 1',
            width: 150,
            headerClassName: 'header-class',
            headerAlign: 'center',
            renderCell: (params) => {
                const cellValue = params.value;
                return (
                    <Tooltip
                        title={params.row.description_1}
                        placement="top">
                        <Chip label={cellValue} variant="filled" size="small" sx={{ color: 'white', fontSize: '0.8rem', backgroundColor: params.row.strength_color_1, width: '100%' }}></Chip>
                    </Tooltip>
                );
            }
        },
        {
            field: 'strength_2',
            headerName: 'Strength 2',
            flex: 1,
            headerClassName: 'header-class',
            headerAlign: 'center',
            renderCell: (params) => {
                const cellValue = params.value;
                return (
                    <Tooltip
                        title={params.row.description_2}
                        placement="top">
                        <Chip label={cellValue} variant="filled" size="small" sx={{ color: 'white', fontSize: '0.8rem', backgroundColor: params.row.strength_color_2, width: '100%' }}></Chip>
                    </Tooltip>
                );
            }
        },
        {
            field: 'strength_3', headerName: 'Strength 3', width: 150, headerClassName: 'header-class', headerAlign: 'center',
            renderCell: (params) => {
                const cellValue = params.value;
                return (
                    <Tooltip
                        title={params.row.description_3}
                        placement="top">
                        <Chip label={cellValue} variant="filled" size="small" sx={{ color: 'white', fontSize: '0.8rem', backgroundColor: params.row.strength_color_3, width: '100%' }}></Chip>
                    </Tooltip>
                );
            }
        },
        {
            field: 'strength_4', headerName: 'Strength 4', width: 150, headerClassName: 'header-class', headerAlign: 'center',
            renderCell: (params) => {
                const cellValue = params.value;
                return (
                    <Tooltip
                        title={params.row.description_4}
                        placement="top">
                        <Chip label={cellValue} variant="filled" size="small" sx={{ color: 'white', fontSize: '0.8rem', backgroundColor: params.row.strength_color_4, width: '100%' }}></Chip>
                    </Tooltip>
                );
            }
        },
        {
            field: 'strength_5', headerName: 'Strength 5', width: 150, headerClassName: 'header-class', headerAlign: 'center',
            renderCell: (params) => {
                const cellValue = params.value;
                return (
                    <Tooltip
                        title={params.row.description_5}
                        placement="top">
                        <Chip label={cellValue} variant="filled" size="small" sx={{ color: 'white', fontSize: '0.8rem', backgroundColor: params.row.strength_color_5, width: '100%' }}></Chip>
                    </Tooltip>
                );
            }
        },
    ];

    const strengthsFixedData = useCallback(() => {
        if (!strengths?.length) return

        const data = strengths.map((strength, index) => ({
            name: strength.name,
            id: index,
            strength_1: strength.strength_1,
            strength_2: strength.strength_2,
            strength_3: strength.strength_3,
            strength_4: strength.strength_4,
            strength_5: strength.strength_5,
            strength_color_1: strength.color_1,
            strength_color_2: strength.color_2,
            strength_color_3: strength.color_3,
            strength_color_4: strength.color_4,
            strength_color_5: strength.color_5,
            description_1: strength.description_1,
            description_2: strength.description_2,
            description_3: strength.description_3,
            description_4: strength.description_4,
            description_5: strength.description_5,
        }));
        data.sort((a, b) => a.name.localeCompare(b.name));
        setRowsFiltered(data);
        setRows(data);
    }, [strengths]);

    useEffect(() => {
        if (strengths) {
            strengthsFixedData()
        }
    }, [strengths, strengthsFixedData]);

    useEffect(() => {
        setNewPageSize()
    }, [pageSizes])

    const setNewPageSize = useCallback((pageSize) => {
        if (pageSize) {
            localStorage.setItem('pageSize', pageSize);
            return
        }
        localStorage.setItem('pageSize', parseInt(localStorage.getItem('pageSize')) || 10);
    }, [paginationModel])

    useEffect(() => {

        if (searchValue) {
            const filteredRows = rows.filter((row) => {
                const strength1 = row.strength_1?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())
                const strength2 = row.strength_2?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())
                const strength3 = row.strength_3?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())
                const strength4 = row.strength_4?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())
                const strength5 = row.strength_5?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())
                const name = row.name?.toLowerCase().includes(searchValue.toLowerCase())
                return strength1 || strength2 || strength3 || strength4 || strength5 || name
            });
            setRowsFiltered(filteredRows);
        } else {
            strengthsFixedData()
        }
    }, [searchValue]);

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, pageSize: parseInt(localStorage.getItem('pageSize')), page: 0 }))
    }, [])

    return (
        <Container sx={{ mt: 10 }}>
            <Card sx={{ p: 2, mb: 2 }}>
                <CardContent>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <SearchField setSearchValue={setSearchValue} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                            rows={rowsFiltered}
                            columns={columns}
                            rowsPerPageOptions={5}
                            pageSizes={[5, 10, 15]}
                            pageSizeOptions={[10, 15, 20]}
                            paginationModel={paginationModel}
                            onPaginationModelChange={(model) => {
                                setPaginationModel((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page }))
                                setNewPageSize(model.pageSize)
                            }}
                            disableSelectionOnClick
                            sx={dataGridStyles}
                        >
                        </DataGrid>
                    </div>
                </CardContent>
            </Card>
        </Container>
    );
}

export default CliftonStrengths;